import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { closeProductModal, refreshProductList } from "../../../redux/features/productModalSlice";
import { AddBrand } from "../../../redux/services/BrandService/brand.Services";
import { ActiveCategoryList } from "../../../redux/services/categoriesService/categories.Services";
import { ActiveChildCategoryList } from "../../../redux/services/categoriesService/categories.Services";
import { ActiveUserList } from "../../../redux/services/User Services/User.Services";
import { ActiveSubCategoryList } from "../../../redux/services/subCategoriesService/subCategories.Services";
import Swal from "sweetalert2";
import { errorToast, successToast } from "../../../Utils/toast";

function CreateBrand({ refreshData }) {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);

  const [formData, setFormData] = useState({
    vendor_id: "",
    category_id: "",
    sub_category_id: "",
    child_category_id: "",
    name: "",
    status: 1,
    logo: null,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await ActiveCategoryList();
      if (res?.success) {
        setCategories(res.data || []);
      }
    } catch (error) {
      console.log(error);
      errorToast("Something went wrong");
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const res = await ActiveSubCategoryList({ category_id: categoryId });
      if (res?.data?.success) {
        setSubCategories(res.data.data || []);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.log(error);
      errorToast("Something went wrong");
    }
  };

  const fetchChildCategories = async (subCategoryId) => {
    try {
      const res = await ActiveChildCategoryList({ sub_category_id: subCategoryId });
      if (res?.data?.success) {
        setChildCategories(res.data.data || []);
      } else {
        setChildCategories([]);
      }
    } catch (error) {
      console.log(error);
      errorToast("Something went wrong");
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await ActiveUserList();
        if (res?.success) {
          setActiveUsers(res?.data || []);
        } else {
          errorToast(res?.message || "Failed to fetch users");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        errorToast(error?.response?.data?.message || "Something went wrong while fetching users");
      }
    };
    fetchUser();
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" && { sub_category_id: "", child_category_id: "" }),
      ...(name === "sub_category_id" && { child_category_id: "" }),
    }));

    if (name === "category_id") {
      setChildCategories([]);
      if (value) {
        await fetchSubCategories(value);
      } else {
        setSubCategories([]);
      }
    }

    if (name === "sub_category_id") {
      if (value) {
        await fetchChildCategories(value);
      } else {
        setChildCategories([]);
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setFormData((prev) => ({ ...prev, logo: file }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = ["Brand Name is required"];
    if (!formData.vendor_id.trim()) newErrors.vendor_id = ["Vendor Name is required"];
    if (!formData.category_id.trim()) newErrors.category_id = ["Please Select Category"];
    if (!formData.sub_category_id.trim()) newErrors.sub_category_id = ["Please Select SubCategory"];
    if (!formData.child_category_id.trim()) newErrors.child_category_id = ["Please Select ChildCategory"];
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setTimeout(() => {
        const firstErrorField = document.querySelector(".is-invalid");
        if (firstErrorField) {
          firstErrorField.focus();
          firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("vendor_id", formData.vendor_id);
      formDataToSend.append("category_id", formData.category_id);
      formDataToSend.append("sub_category_id", formData.sub_category_id);
      formDataToSend.append("child_category_id", formData.child_category_id);
      formDataToSend.append("name", formData.name);
      formDataToSend.append("status", formData.status);
      if (logoFile) formDataToSend.append("logo", logoFile);

      const res = await AddBrand(formDataToSend);
      if (res?.success === true) {
        successToast(res?.message);
        dispatch(closeProductModal());
        dispatch(refreshProductList());
      } else {
        errorToast(res?.data?.message || "Failed to create brand");
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      if (error?.response?.status === 422) {
        setErrors(error.response.data.errors || {});
        errorToast("Validation Error", "Please check the form fields", "error");
      } else {
        errorToast("Error", error?.response?.data?.message || "Something went wrong", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main_page">
      <form onSubmit={handleSubmit} className="row g-3">
        {/* Vendor */}
        <div className="col-md-6">
          <label>Vendor <span className="text-danger">*</span></label>
          <select
            className={`form-control ${errors?.vendor_id ? "is-invalid" : ""}`}
            name="vendor_id"
            value={formData.vendor_id}
            onChange={handleChange}
          >
            <option value="">Select Vendor</option>
            {activeUsers.map((item) => (
              <option key={item.id} value={item.id}>{item.name||item.mobile}</option>
            ))}
          </select>
          {errors?.vendor_id && (
            <div className="invalid-feedback d-block">{errors.vendor_id[0]}</div>
          )}
        </div>

        {/* Category */}
        <div className="col-md-6">
          <label>Category <span className="text-danger">*</span></label>
          <select
            className={`form-control ${errors?.category_id ? "is-invalid" : ""}`}
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
          >
            <option value="">Select Category</option>
            {categories.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          {errors?.category_id && (
            <div className="invalid-feedback d-block">{errors.category_id[0]}</div>
          )}
        </div>

        {/* Sub Category */}
        <div className="col-md-6">
          <label>Sub Category <span className="text-danger">*</span></label>
          <select
            className={`form-control ${errors?.sub_category_id ? "is-invalid" : ""}`}
            name="sub_category_id"
            value={formData.sub_category_id}
            onChange={handleChange}
            disabled={!formData.category_id}
          >
            <option value="">Select Sub Category</option>
            {subCategories.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          {errors?.sub_category_id && (
            <div className="invalid-feedback d-block">{errors.sub_category_id[0]}</div>
          )}
        </div>

        {/* Child Category */}
        <div className="col-md-6">
          <label>Child Category <span className="text-danger">*</span></label>
          <select
            className={`form-control ${errors?.child_category_id ? "is-invalid" : ""}`}
            name="child_category_id"
            value={formData.child_category_id}
            onChange={handleChange}
            disabled={!formData.sub_category_id}
          >
            <option value="">Select Child Category</option>
            {childCategories.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          {errors?.child_category_id && (
            <div className="invalid-feedback d-block">{errors.child_category_id[0]}</div>
          )}
        </div>

        {/* Brand Name */}
        <div className="col-md-6">
          <label>Brand Name <span className="text-danger">*</span></label>
          <input
            placeholder="Brand"
            className={`form-control ${errors?.name ? "is-invalid" : ""}`}
            value={formData.name}
            onChange={handleChange}
            name="name"
            disabled={loading}
          />
          {errors?.name && (
            <div className="invalid-feedback d-block">
              {Array.isArray(errors.name) ? errors.name[0] : errors.name}
            </div>
          )}
        </div>

        {/* Logo */}
        <div className="col-md-6">
          <label>Logo</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={handleLogoChange}
            disabled={loading}
          />
          {logoFile && (
            <div className="mt-2">
              <img
                src={URL.createObjectURL(logoFile)}
                alt="Logo preview"
                style={{ width: "100px", height: "100px", objectFit: "contain" }}
              />
            </div>
          )}
        </div>

        {/* Status */}
        <div className="mb-3">
          <label>Status</label>
          <select
            className="form-control"
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={loading}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>

        <button className="add-btn" type="submit" disabled={loading}>
          {loading ? "Creating..." : "Create Brand"}
        </button>
      </form>
    </div>
  );
}

export default CreateBrand;